import { useContext, createContext, useState, SetStateAction, Dispatch, useCallback, useMemo } from 'react';

import { JobsAggregationQueryJobDataType, JobsAggregationStatus, JobsService } from 'services/jobs';
import { StringifiedUUID } from 'types';
import { APIError } from 'utils/errors';

export type JobBannerType = 'export' | 'create-scenario';

export type JobsAggregationContextType = {
  jobsAggregationId: StringifiedUUID;
  setJobsAggregationId: Dispatch<SetStateAction<StringifiedUUID>>;
  pollJobsAggregationQuery: (jobId: StringifiedUUID, jobType?: JobBannerType) => void;
  jobsAggregationData: Array<JobsAggregationQueryJobDataType>;
  setJobsAggregationData: Dispatch<SetStateAction<Array<JobsAggregationQueryJobDataType>>>;
  jobsStatus: JobsStatus;
  jobsActive: boolean;
  jobType: JobBannerType | null;
  setJobType: Dispatch<SetStateAction<JobBannerType | null>>;
  setJobsPendingState: (jobId: StringifiedUUID, jobType?: JobBannerType) => void;
  setJobsSuccessState: (data: Array<JobsAggregationQueryJobDataType>) => void;
  setJobsFailureState: () => void;
  setJobsNotStartedState: () => void;
  setJobsStatus: Dispatch<SetStateAction<JobsStatus>>;
};

// TODO: dedupe with types/jobs.ts once confirming direction with jesse
export enum JobsStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  PENDING = 'PENDING',
  NOT_STARTED = 'NOT_STARTED',
}

const JobsAggregationContext = createContext<JobsAggregationContextType>({} as JobsAggregationContextType);
export const useJobsAggregationContext = () => useContext(JobsAggregationContext);
const MAX_POLLING_COUNT = 15;

export const JobsAggregationContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobsAggregationId, setJobsAggregationId] = useState<StringifiedUUID>('');
  const [jobsAggregationData, setJobsAggregationData] = useState<Array<JobsAggregationQueryJobDataType>>([]);
  const [jobsStatus, setJobsStatus] = useState<JobsStatus>(JobsStatus.NOT_STARTED);
  const [jobType, setJobType] = useState<JobBannerType | null>(null);

  const setJobsPendingState = useCallback((jobId: StringifiedUUID, type: JobBannerType = 'export'): void => {
    setJobsStatus(JobsStatus.PENDING);
    setJobsAggregationData([]);
    setJobsAggregationId(jobId);
    setJobType(type);
  }, []);

  const setJobsNotStartedState = useCallback((): void => {
    setJobsStatus(JobsStatus.NOT_STARTED);
    setJobsAggregationId('');
    setJobsAggregationData([]);
    setJobType(null);
  }, []);

  const setJobsFailureState = useCallback((): void => {
    setJobsStatus(JobsStatus.FAILURE);
    setJobsAggregationData([]);
    setJobsAggregationId('');
  }, []);

  const setJobsSuccessState = useCallback((data: Array<JobsAggregationQueryJobDataType>): void => {
    setJobsStatus(JobsStatus.SUCCESS);
    setJobsAggregationData(data);
  }, []);

  const pollJobsAggregationQuery = useCallback(
    (jobId: StringifiedUUID, type: JobBannerType = 'export') => {
      let count = 0;
      setJobsPendingState(jobId, type);
      const intervalRef = setInterval(
        async () => {
          if (count >= MAX_POLLING_COUNT) {
            clearInterval(intervalRef);
            setJobsFailureState();
            throw new APIError({
              type: 'api_error',
              message: 'Failed to retrieve job results',
              status: 500,
            });
          }
          try {
            const data = await JobsService.getJobsAggregationQuery(jobId);
            if (data.status !== JobsAggregationStatus.SUCCESS) {
              count += 1;
              return;
            }
            setJobsSuccessState(data.data);
            clearInterval(intervalRef);
          } catch (e) {
            setJobsFailureState();
            clearInterval(intervalRef);
          }
        },
        1000,
        [jobId],
      );
    },
    [setJobsPendingState, setJobsFailureState, setJobsSuccessState],
  );
  const jobsActive =
    jobsStatus === JobsStatus.SUCCESS || jobsStatus === JobsStatus.FAILURE || jobsStatus === JobsStatus.PENDING;

  const contextValue = useMemo(
    () => ({
      jobsAggregationId,
      pollJobsAggregationQuery,
      setJobsAggregationId,
      jobsAggregationData,
      setJobsAggregationData,
      jobsStatus,
      jobsActive,
      jobType,
      setJobType,
      setJobsPendingState,
      setJobsSuccessState,
      setJobsFailureState,
      setJobsStatus,
      setJobsNotStartedState,
    }),
    [
      jobsAggregationId,
      jobsAggregationData,
      jobsStatus,
      jobsActive,
      jobType,
      pollJobsAggregationQuery,
      setJobsPendingState,
      setJobsSuccessState,
      setJobsFailureState,
      setJobsNotStartedState,
    ],
  );

  return <JobsAggregationContext.Provider value={contextValue}>{children}</JobsAggregationContext.Provider>;
};
